import { OverviewEntity, OverviewTreeNode } from "./brandOverview.types";

/**
 * Nests a flat list of entities into a parent/child hierarchy using each
 * entity's `parentId`. Entities whose parent is missing from the list surface
 * as top-level roots. A visited set guards against relation cycles.
 */
export const buildOverviewTree = (
  entities: OverviewEntity[]
): OverviewTreeNode[] => {
  const idSet = new Set(entities.map((entity) => entity.id));

  const childrenOf = new Map<string, OverviewEntity[]>();
  entities.forEach((entity) => {
    const parentId = entity.parentId;
    if (!parentId || !idSet.has(parentId)) return;

    const list = childrenOf.get(parentId) ?? [];
    list.push(entity);
    childrenOf.set(parentId, list);
  });

  const visited = new Set<string>();

  const buildNode = (entity: OverviewEntity): OverviewTreeNode => {
    visited.add(entity.id);
    const kids = (childrenOf.get(entity.id) ?? [])
      .filter((kid) => !visited.has(kid.id))
      .map(buildNode);

    return { entity, children: kids };
  };

  const isRoot = (entity: OverviewEntity): boolean =>
    !entity.parentId || !idSet.has(entity.parentId);

  return entities.filter(isRoot).map(buildNode);
};
